import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import logo from '../assets/logo_blau.png';
import { API_CONFIG } from '../config/config';
import MFAVerification from '../components/MFAVerification';

const validationSchema = yup.object({
  email: yup
    .string()
    .email('Geben Sie eine gültige E-Mail-Adresse ein')
    .required('E-Mail-Adresse ist erforderlich'),
  password: yup
    .string()
    .when('step', {
      is: 2,
      then: (schema) => schema
        .min(12, 'Das Passwort sollte mindestens 12 Zeichen lang sein')
        .max(128, 'Das Passwort sollte nicht länger als 128 Zeichen sein')
        .matches(/^[\s\S]*$/, 'Das Passwort kann beliebige Zeichen enthalten')
        .required('Passwort ist erforderlich'),
      otherwise: (schema) => schema.notRequired()
    })
});

const Login = () => {
  const navigate = useNavigate();
  const { login, error: authError, loading: authLoading, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [csrfToken, setCsrfToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState({ email: '', password: '' });

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Update MFA required state when authError changes
  useEffect(() => {
    if (authError === 'MFA-Code erforderlich') {
      setMfaRequired(true);
    }
  }, [authError]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleStep1 = async (values) => {
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/login/step1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: values.email.toLowerCase().trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          // Handle rate limit error
          const retryAfter = response.headers.get('Retry-After');
          const waitTime = retryAfter ? parseInt(retryAfter) : 60;
          throw new Error(`Zu viele Anfragen. Bitte warten Sie ${waitTime} Sekunden, bevor Sie es erneut versuchen.`);
        }
        throw new Error('Anmelden fehlgeschlagen');
      }

      setCsrfToken(data.csrf_token);
      setCredentials(prev => ({ ...prev, email: values.email }));
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (values) => {
    setError('');
    setLoading(true);

    try {
      // Store credentials before attempting login
      const normalizedEmail = values.email.toLowerCase().trim();
      setCredentials({
        email: normalizedEmail,
        password: values.password
      });
      
      const success = await login(normalizedEmail, values.password, csrfToken);
      
      if (!success) {
        if (authError === 'MFA-Code erforderlich') {
          return; // MFA form will be shown by useEffect
        }
        throw new Error(authError || 'Anmelden fehlgeschlagen');
      }
      
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMFAVerify = async (code) => {
    setError('');
    setLoading(true);

    try {
      // Get a new CSRF token for MFA verification
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/login/step1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: credentials.email.toLowerCase().trim() }),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Abrufen des neuen CSRF-Tokens');
      }

      const data = await response.json();
      const newCsrfToken = data.csrf_token;
      
      // Now verify MFA with the new token
      console.log('Verifizieren Sie den MFA-Code mit:', {
        email: credentials.email,
        csrfToken: newCsrfToken,
        mfaCode: code,
        hasPassword: !!credentials.password
      });
      
      const success = await login(credentials.email, credentials.password, newCsrfToken, code);
      
      if (!success) {
        if (authError === 'Ungültiger MFA-Code') {
          throw new Error('Ungültige MFA-Code');
        }
        throw new Error(authError || 'MFA-Verifizierung fehlgeschlagen');
      }
      
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMFACancel = () => {
    setMfaRequired(false);
    setStep(1);
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      step: 1
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (step === 1) {
        handleStep1(values);
      } else {
        handleStep2(values);
      }
    },
  });

  // Update formik's step value when step changes
  useEffect(() => {
    formik.setFieldValue('step', step);
  }, [step]);

  if (mfaRequired) {
    return (
      <MFAVerification
        onVerify={handleMFAVerify}
        onCancel={handleMFACancel}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <img
            src={logo}
            alt="cyberlagebild.de Logo"
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </Box>
        <Typography variant="h6" component="h2" gutterBottom align="center">
          Anmelden
        </Typography>
        {(error || authError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || authError}
          </Alert>
        )}
        <form onSubmit={formik.handleSubmit} noValidate>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="E-Mail-Adresse"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            margin="normal"
            disabled={step === 2}
            autoComplete="email"
          />
          {step === 2 && (
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Passwort"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              margin="normal"
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Button
            color="primary"
            variant="contained"
            fullWidth
            type="submit"
            disabled={loading || (step === 1 && !formik.values.email) || (step === 2 && !formik.values.password)}
            sx={{ 
              mt: 3,
              backgroundColor: 'blue',
              '&:hover': {
                backgroundColor: 'blue',
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : step === 1 ? (
              'Weiter'
            ) : (
              'Anmelden'
            )}
          </Button>
          {step === 2 && (
            <Button
              color="secondary"
              variant="text"
              fullWidth
              onClick={() => setStep(1)}
              sx={{ mt: 1 }}
            >
              Zurück
            </Button>
          )}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link component={RouterLink} to="/register" variant="body2">
              Kein Konto? Registriere dich hier
            </Link>
            <Box sx={{ mt: 1 }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                Passwort vergessen?
              </Link>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default Login; 